// TimeGrid — calendario columnas × eje de horas con bloques proporcionales (estilo Kydemy).
// Motor portado de la agenda dental (DailyViewDesktop): top/height en px por minutos,
// línea de "ahora" con refresco por minuto + auto-scroll, carriles para solapes (lanes.ts).
// Genérico por diseño (plan PLAN-TIMEGRID.md): columnas = salones (vista día) o días (vista semana).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { computeLanes, laneStyle } from './lanes';
import { colorClase } from '../../utils/classColor';

export interface TimeGridColumn {
  id: string;
  label: React.ReactNode;
  highlight?: boolean; // p.ej. la columna de HOY en la vista semana
}

export interface TimeGridBlock {
  key: string;
  colId: string;
  startMin: number; // minutos desde medianoche
  endMin: number;
  color?: string | null;
  dimmed?: boolean; // clase ya terminada → atenuada (patrón de la agenda)
  muted?: boolean;  // renta/ensayo/privada → capa secundaria (gris rayado, no compite con clases)
  ring?: boolean;   // resaltar (p.ej. "en curso")
  subCol?: string;  // sub-columna fija dentro de su columna (p.ej. el salón) — ver prop subCols
  onClick?: () => void;
  content: React.ReactNode;
}

interface Props {
  columns: TimeGridColumn[];
  blocks: TimeGridBlock[];
  hourHeight?: number;     // px por hora
  maxHeight?: number;      // alto máx del área scrolleable (px); sin límite si se omite
  nowLine?: boolean;       // línea de hora actual (solo tiene sentido si el grid representa HOY o incluye hoy)
  autoScrollNow?: boolean; // al montar, centrar el scroll en "ahora"
  minColWidth?: number;    // px mínimos por columna (scroll horizontal en pantallas chicas)
  hourStart?: number;      // fijar inicio del eje (se expande si hay clases antes)
  hourEnd?: number;        // fijar fin del eje (se expande si hay clases después)
  // Con rango fijo: en vez de expandir el eje por bloques anómalos (p.ej. una
  // clase capturada a la 01:15 AM), ocultarlos y avisar en una nota al pie.
  hideOutOfRange?: boolean;
  // Clic en un HUECO del calendario (Karla 30-jul, como Kydemy: «pongo el cursor a la una,
  // le doy al + y me abre el formulario»). Si no se pasa, el grid se comporta como siempre
  // (el Home y el screensaver lo usan solo para mirar).
  // startMin viene redondeado a 30 minutos.
  onSlotClick?: (colId: string, startMin: number) => void;
  // SUB-COLUMNAS FIJAS (Karla 29-sep-2026: «que siempre se respete la cajita por salón… me
  // ayuda a visualizar que tengo estos espacios vacíos»). Cada columna se parte en N franjas
  // fijas (una por salón) y cada bloque cae en la suya según `subCol`: una clase sola en el
  // Salón 1 ocupa SU mitad y la del Salón 2 se ve vacía. Sin esto los carriles se reparten
  // por traslape y una clase sola se estira a todo el ancho. Con 0–1 sub-columnas no cambia nada.
  subCols?: { id: string; label: React.ReactNode }[];
}

/** Granularidad del hueco clickeable (min). */
const SLOT_MIN = 30;
const hhmmOf = (m: number) => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

const currentMinutes = () => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); };

/** Texto legible sobre el color sólido del bloque (misma idea que contrastText del mailer). */
export function blockText(hex?: string | null): string {
  const h = String(hex || '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return '#ffffff';
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.55 ? '#1f2937' : '#ffffff';
}

export default function TimeGrid({
  columns, blocks: allBlocks, hourHeight = 56, maxHeight, nowLine = false, autoScrollNow = false, minColWidth = 110,
  hourStart: fixedStart, hourEnd: fixedEnd, hideOutOfRange = false, onSlotClick, subCols,
}: Props) {
  const subs = subCols && subCols.length > 1 ? subCols : null;
  const subIdx = (b: TimeGridBlock) => {
    if (!subs) return 0;
    const i = subs.findIndex((x) => x.id === b.subCol);
    return i < 0 ? 0 : i;
  };
  // Bloques fuera del rango fijo (si hideOutOfRange): se excluyen del grid y se avisan al pie.
  const clamp = hideOutOfRange && fixedStart != null && fixedEnd != null;
  const blocks = useMemo(() =>
    clamp ? allBlocks.filter((b) => b.endMin > fixedStart! * 60 && b.startMin < fixedEnd! * 60) : allBlocks,
    [allBlocks, clamp, fixedStart, fixedEnd]);
  const outliers = clamp ? allBlocks.length - blocks.length : 0;
  const [nowMin, setNowMin] = useState(currentMinutes());
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrolledRef = useRef(false);

  useEffect(() => {
    const id = setInterval(() => setNowMin(currentMinutes()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Rango horario: fijo si viene por props (hourStart/hourEnd) pero se EXPANDE si hay
  // clases fuera de él; sin props se deriva de los datos (media hora de aire), fallback 8–22.
  const { hourStart, hourEnd } = useMemo(() => {
    let start = fixedStart ?? 8;
    let end = fixedEnd ?? 22;
    if (clamp) return { hourStart: start, hourEnd: end }; // rango fijo estricto
    if (blocks.length) {
      const min = Math.min(...blocks.map((b) => b.startMin));
      const max = Math.max(...blocks.map((b) => b.endMin));
      // Con rango fijo, expandir solo hasta el dato exacto (sin aire) para no
      // colgar una fila vacía extra; sin rango fijo, media hora de aire por lado.
      start = fixedStart != null
        ? Math.min(fixedStart, Math.max(0, Math.floor(min / 60)))
        : Math.max(0, Math.floor((min - 30) / 60));
      end = fixedEnd != null
        ? Math.max(fixedEnd, Math.min(24, Math.ceil(max / 60)))
        : Math.min(24, Math.ceil((max + 30) / 60));
    }
    return { hourStart: start, hourEnd: end };
  }, [blocks, fixedStart, fixedEnd, clamp]);

  const totalPx = (hourEnd - hourStart) * hourHeight;
  const hours = Array.from({ length: hourEnd - hourStart }, (_, i) => hourStart + i);
  const toPx = (min: number) => ((min - hourStart * 60) / 60) * hourHeight;
  const nowPx = nowMin >= hourStart * 60 && nowMin <= hourEnd * 60 ? toPx(nowMin) : null;

  // ===== Hueco clickeable (solo si el caller pasó onSlotClick) =====
  // Capa de fondo por columna, DEBAJO de los bloques: así el clic sobre una clase nunca
  // se confunde con "agregar aquí", y al pasar el cursor por un hueco se ve la franja + «+».
  const [hoverSlot, setHoverSlot] = useState<{ colId: string; startMin: number } | null>(null);
  const slotFromY = (y: number) => {
    const raw = hourStart * 60 + (y / hourHeight) * 60;
    const snapped = Math.floor(raw / SLOT_MIN) * SLOT_MIN;          // redondeo a 30 min
    return Math.min(hourEnd * 60 - SLOT_MIN, Math.max(hourStart * 60, snapped));
  };
  const slotAtEvent = (e: React.MouseEvent<HTMLDivElement>) =>
    slotFromY(e.clientY - e.currentTarget.getBoundingClientRect().top);

  // Auto-scroll a "ahora" al montar (una sola vez, como la agenda).
  useEffect(() => {
    if (!autoScrollNow || scrolledRef.current || nowPx === null) return;
    const el = scrollRef.current;
    if (!el) return;
    const t = setTimeout(() => {
      el.scrollTo({ top: Math.max(0, nowPx - el.clientHeight / 2), behavior: 'auto' });
      scrolledRef.current = true;
    }, 80);
    return () => clearTimeout(t);
  }, [autoScrollNow, nowPx]);

  // Lanes por columna (los solapes dentro de una columna se reparten el ancho).
  const lanesByCol = useMemo(() => {
    const m = new Map<string, Map<TimeGridBlock, { laneIndex: number; totalLanes: number }>>();
    for (const col of columns) {
      const colBlocks = blocks.filter((b) => b.colId === col.id);
      if (!subs) { m.set(col.id, computeLanes(colBlocks)); continue; }
      // Con sub-columnas los traslapes se resuelven DENTRO de cada salón, no entre salones.
      const merged = new Map<TimeGridBlock, { laneIndex: number; totalLanes: number }>();
      subs.forEach((_, i) => computeLanes(colBlocks.filter((b) => subIdx(b) === i)).forEach((v, k) => merged.set(k, v)));
      m.set(col.id, merged);
    }
    return m;
  }, [columns, blocks, subs]);

  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
      <div className="overflow-x-auto">
        <div style={{ minWidth: 48 + columns.length * minColWidth }}>
          {/* Encabezados de columna */}
          <div className="flex border-b border-line bg-surface-2/60">
            <div className="w-12 shrink-0 border-r border-line" />
            {columns.map((c) => (
              <div key={c.id}
                className={`flex-1 border-r border-line px-1 py-2 text-center text-xs font-semibold last:border-r-0 ${c.highlight ? 'bg-accent/15 text-accent-ink' : 'text-content-2'}`}>
                {c.label}
                {subs && (
                  <div className="mt-1 flex text-[9px] font-medium uppercase tracking-wide text-content-3">
                    {subs.map((x) => <div key={x.id} className="flex-1 truncate">{x.label}</div>)}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Cuerpo scrolleable: eje de horas + columnas con bloques absolutos */}
          <div ref={scrollRef} className="overflow-y-auto" style={maxHeight ? { maxHeight } : undefined}>
            <div className="flex" style={{ height: totalPx }}>
              {/* Eje de horas */}
              <div className="relative w-12 shrink-0 border-r border-line bg-surface">
                {hours.map((h) => (
                  <div key={h} className="absolute w-full pr-1 text-right text-[10px] text-content-3"
                    style={{ top: toPx(h * 60) + 2 }}>
                    {h}:00
                  </div>
                ))}
              </div>

              {/* Columnas */}
              <div className="relative flex flex-1">
                {/* Gridlines horizontales */}
                {hours.map((h) => (
                  <div key={h} className="pointer-events-none absolute inset-x-0 border-t border-line/60"
                    style={{ top: toPx(h * 60) }} />
                ))}

                {columns.map((col) => {
                  const lanes = lanesByCol.get(col.id);
                  const colBlocks = blocks.filter((b) => b.colId === col.id);
                  return (
                    <div key={col.id}
                      className={`relative flex-1 border-r border-line last:border-r-0 ${col.highlight ? 'bg-accent/5' : ''}`}>
                      {/* Capa de huecos: va ANTES de los bloques → queda debajo de ellos */}
                      {onSlotClick && (
                        <div
                          className="absolute inset-0 cursor-pointer"
                          title="Clic para agregar una clase a esta hora"
                          onMouseMove={(e) => {
                            const s = slotAtEvent(e);
                            setHoverSlot((h) => (h && h.colId === col.id && h.startMin === s ? h : { colId: col.id, startMin: s }));
                          }}
                          onMouseLeave={() => setHoverSlot((h) => (h?.colId === col.id ? null : h))}
                          onClick={(e) => {
                            // Defensa extra: si el clic viene de un bloque de clase, no es un hueco.
                            if ((e.target as HTMLElement).closest('button')) return;
                            onSlotClick(col.id, slotAtEvent(e)); // en táctil el tap entra por aquí igual
                          }}
                        >
                          {hoverSlot?.colId === col.id && (
                            <div
                              className="pointer-events-none absolute inset-x-[3px] flex items-center justify-center gap-0.5 overflow-hidden whitespace-nowrap rounded-md border border-dashed border-accent/70 bg-accent/20 text-[10px] font-semibold leading-none text-accent-ink"
                              style={{ top: toPx(hoverSlot.startMin), height: Math.max(16, (SLOT_MIN / 60) * hourHeight - 1) }}
                            >
                              <Plus size={11} className="shrink-0" />
                              {hhmmOf(hoverSlot.startMin)} – {hhmmOf(hoverSlot.startMin + 60)}
                            </div>
                          )}
                        </div>
                      )}
                      {/* Divisores punteados entre salones: el hueco vacío se lee como «salón libre» */}
                      {subs && subs.slice(1).map((x, i) => (
                        <div key={x.id} className="pointer-events-none absolute inset-y-0 border-l border-dashed border-line/70"
                          style={{ left: `${((i + 1) * 100) / subs.length}%` }} />
                      ))}
                      {colBlocks.map((b) => {
                        const info = lanes?.get(b) || { laneIndex: 0, totalLanes: 1 };
                        const top = toPx(b.startMin);
                        const height = Math.max(14, toPx(b.endMin) - toPx(b.startMin) - 2);
                        const bg = colorClase(b.color);
                        return (
                          <button key={b.key} type="button" onClick={b.onClick}
                            className={`absolute overflow-hidden rounded-md text-left leading-tight transition hover:brightness-110 hover:z-20 ${b.ring ? 'ring-2 ring-accent z-10' : ''}`}
                            style={{
                              top, height, ...laneStyle(info.laneIndex, info.totalLanes, subs ? { index: subIdx(b), total: subs.length } : undefined),
                              background: bg, color: blockText(bg),
                              // Capa secundaria: rayado gris con los tokens del tema (funciona en claro/oscuro/glass).
                              // Con surface-2 no se distinguía del fondo en claro (Mabel 8-oct: «un grisito más subido»).
                              ...(b.muted ? {
                                background: 'repeating-linear-gradient(45deg, rgb(var(--c-content-3) / .32) 0 6px, rgb(var(--c-content-3) / .20) 6px 12px)',
                                color: 'rgb(var(--c-content))',
                                border: '1px solid rgb(var(--c-content-3) / .55)',
                              } : {}),
                              ...(b.dimmed ? { opacity: 0.45, filter: 'saturate(80%)' } : {}),
                            }}>
                            {b.content}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}

                {/* Línea de hora actual */}
                {nowLine && nowPx !== null && (
                  <div className="pointer-events-none absolute inset-x-0 z-30" style={{ top: nowPx }}>
                    <div className="relative border-t-2 border-red-500">
                      <span className="absolute -left-1 -top-[5px] h-2 w-2 rounded-full bg-red-500" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {outliers > 0 && (
        <p className="border-t border-line bg-surface-2/50 px-3 py-1.5 text-[11px] text-content-3">
          {outliers} horario{outliers > 1 ? 's' : ''} fuera de {fixedStart}:00–{fixedEnd}:00 no se muestra{outliers > 1 ? 'n' : ''} aquí (revisar su captura en la clase).
        </p>
      )}
    </div>
  );
}
